import { DailyBonusModalProps } from '../Shared/types';
import {
  StyledPopup,
  StyledDailyBonusModal,
  StyledDailyBonusBorder,
} from './style';
import { ActionButton } from '../../../components/ActionButton';
import { forwardRef, useImperativeHandle } from 'react';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { motion } from 'framer-motion';
import { EFontWeights } from 'utils/enums';
import { createGradientOrSolid } from 'utils';
import { AutoTextSize } from 'utils/autoTextSizeUtils';
import { useDailyBonus } from '../Shared/hook';
import {
  bannerVariantsAnimation,
  formatTime,
  productVariantsAnimation,
} from '../Shared/utils';
import { PopupSingleProduct } from './PopupSingleProduct';
import lockIcon from 'assets/SpinX/lock.png';

export const SpinxDailyBonus = forwardRef(
  (props: DailyBonusModalProps, ref) => {
    const {
      title,
      titleColor = 'white',
      titleWeight = EFontWeights.Bold,
      titleSize = '24px',
      backgroundImage,
      borderColor,
      borderWidth = 2,
      buttonText = 'Collect',
      products,
      modalContainer,
      isPreview = false,
      nextProducts,
      showCloseButton = true,
      isBadgeNextProduct = false,
      shouldDisplayProductsAnimation = false,
      dataTestId,
      className = '',
    } = props;

    const {
      open,
      showNextProduct,
      timeLeft,
      startExitAnimProduct,
      startExitAnimModal,
      numberTitleSize,
      onCloseClick,
      onCollectClicked,
      onOrderSuccess,
      onOrderFail,
    } = useDailyBonus(props);

    useImperativeHandle(
      ref,
      (): { onOrderFail: () => void; onOrderSuccess: () => void } => ({
        onOrderSuccess,
        onOrderFail,
      }),
    );

    return (
      <StyledDailyBonusModal
        disableEnforceFocus={isPreview}
        className={`daily-bonus-modal ${
          isPreview ? 'daily-bonus-modal__preview modal-preview' : ''
        }`}
        open={open}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        container={
          modalContainer
            ? modalContainer
            : () => document.getElementById('root')
        }
      >
        <StyledDailyBonusBorder
          variants={bannerVariantsAnimation}
          animate={startExitAnimModal ? 'exit' : 'enter'}
          className={`daily-bonus-border`}
          style={{
            background:
              borderWidth === 0
                ? 'transparent'
                : borderColor
                ? createGradientOrSolid(borderColor)
                : 'var(--bundle-border-color)',
            padding: borderWidth + 'px',
          }}
        >
          <StyledPopup
            titleColor={titleColor}
            titleWeight={titleWeight}
            titleSize={titleSize}
            backgroundImage={backgroundImage}
            data-testid={dataTestId}
          >
            <div className={`daily-bonus-banner`}>
              {(showCloseButton || showNextProduct) && (
                <button
                  className={'daily-bonus-banner__close-icon'}
                  onClick={() => onCloseClick()}
                >
                  <ClearOutlinedIcon
                    className="closeButton"
                    style={{
                      color: titleColor,
                    }}
                  />
                </button>
              )}
              <div className="daily-bonus-banner__title">
                <AutoTextSize
                  maxFontSizePx={numberTitleSize}
                  minFontSizePx={4}
                  as="div"
                  mode="oneline"
                >
                  {title}
                </AutoTextSize>
              </div>
              {showNextProduct && nextProducts && nextProducts.length > 0 ? (
                <>
                  <motion.div
                    animate={
                      shouldDisplayProductsAnimation
                        ? { opacity: [0, 1] }
                        : { scale: [0, 1.1, 1] }
                    }
                    transition={
                      shouldDisplayProductsAnimation
                        ? { duration: 0.6, delay: 0.5 }
                        : {
                            duration: 0.3,
                            delay: 0.2,
                          }
                    }
                    className="daily-bonus-banner__product-container"
                  >
                    <PopupSingleProduct
                      quantityDisplay={nextProducts[0].quantityDisplay}
                      images={nextProducts[0]?.images}
                      textColor={nextProducts[0]?.textFontColorHex}
                    />
                  </motion.div>
                  <ActionButton
                    className="daily-bonus-banner__action-button daily-bonus-banner__action-button--disabled"
                    text={formatTime(timeLeft || 0)}
                    colors={{ colorOne: 'grey' }}
                    borderRadius={'7px'}
                    width={'100%'}
                    icon={lockIcon}
                    isLoading={!timeLeft}
                  />
                </>
              ) : (
                !isBadgeNextProduct && (
                  <>
                    {/* todo: add support in multiple products when needed */}
                    {products && products?.length > 0 && (
                      <motion.div
                        variants={productVariantsAnimation}
                        animate={
                          startExitAnimProduct &&
                          !shouldDisplayProductsAnimation
                            ? 'exit'
                            : 'regular'
                        }
                        transition={{
                          duration: 0.3,
                          ease: 'easeInOut',
                        }}
                        className="daily-bonus-banner__product-container"
                      >
                        <PopupSingleProduct
                          quantityDisplay={products[0].quantityDisplay}
                          images={products[0]?.images}
                          textColor={products[0]?.textFontColorHex}
                        />
                      </motion.div>
                    )}
                    <motion.div
                      animate={{ scale: [0, 1, 1.05, 1] }}
                      transition={{
                        duration: 0.4,
                        delay: 0.2,
                      }}
                      style={{ width: '100%' }}
                    >
                      <motion.div whileHover={{ scale: 0.9 }}>
                        <ActionButton
                          className="daily-bonus-banner__action-button"
                          text={buttonText}
                          action={onCollectClicked}
                          borderRadius={'7px'}
                          width={'100%'}
                        />
                      </motion.div>
                    </motion.div>
                  </>
                )
              )}
            </div>
          </StyledPopup>
        </StyledDailyBonusBorder>
      </StyledDailyBonusModal>
    );
  },
);
